<template>
  <app-content class="my-data">
    <app-panel>
      <h3 slot="panel-header">
        {{ $t('my-data.information-header') }}
      </h3>
      <b-container class="my-data__body" slot="panel-body" fluid>
        <b-row class="my-data__fields">
          <b-col class="form-section-title">
            {{ $t('my-data.general-data') }}
          </b-col>
          <div class="w-100"/>
          <b-col
            role="group"
            class="my-data__field-group"
            sm="9"
            md="7"
            lg="5"
            xl="4"
          >
            <label class="my-data__field-label" for="name">
              {{ $t('my-data.name') }}*
            </label>
            <b-input
              id="name"
              v-model="userData.name"
              maxlength="100"
              :state="$bsStateValidator($v.userData.name.$error)"
              @blur.native="$v.userData.name.$touch()"
            />
            <b-tooltip
              :show="showNameSurnameTooltip($v.userData.name)"
              target="name" triggers=""
              placement="right"
            >
              {{
                $t('messages.MG001.message', {
                  0: 2,
                  1: 100,
                })
              }}
            </b-tooltip>
          </b-col>
          <div class="w-100"/>
          <b-col
            role="group"
            class="my-data__field-group"
            sm="9"
            md="7"
            lg="5"
            xl="4"
          >
            <label class="my-data__field-label" for="surname">
              {{ $t('my-data.surname') }}*
            </label>
            <b-input
              id="surname" v-model="userData.surname"
              maxlength="200"
              :state="$bsStateValidator($v.userData.surname.$error)"
              @blur.native="$v.userData.surname.$touch()"
            />
            <b-tooltip
              :show="showNameSurnameTooltip($v.userData.surname)"
              target="surname" triggers=""
              placement="right"
            >
              {{
                $t('messages.MG001.message', {
                  0: 2,
                  1: 200,
                })
              }}
            </b-tooltip>
          </b-col>
          <div class="w-100"/>
          <b-col
            role="group"
            class="my-data__field-group"
            sm="9"
            md="7"
            lg="5"
            xl="4"
          >
            <label class="my-data__field-label" for="language">
              {{ $t('my-data.language') }}
            </label>
            <language-select
              id="language"
              v-model="userData.language"
            />
          </b-col>
          <div class="w-100"/>
          <b-col
            role="group"
            class="my-data__field-group"
            sm="9"
            md="7"
            lg="5"
            xl="4"
          >
            <label class="my-data__field-label" for="timezone">
              {{ $t('my-data.timezone') }}
              <info-badge
                class="my-data__field-tooltip"
                translation-key="messages.MI086.message"
              />
            </label>
            <timezone-select
              id="timezone"
              v-model="userData.timezoneId"
            />
          </b-col>
          <div class="w-100"/>
          <b-col class="form-section-title">
            {{ $t('my-data.contact') }}
          </b-col>
          <div class="w-100"/>
          <b-col
            role="group"
            class="my-data__field-group"
            sm="9"
            md="7"
            lg="5"
            xl="4"
          >
            <label class="my-data__field-label" for="email">
              {{ $t('my-data.email') }}*
            </label>
            <b-input
              id="email" v-model="userData.email"
              :state="$bsStateValidator($v.userData.email.$error)"
              @blur.native="$v.userData.email.$touch()"
            />
            <b-tooltip
              :show="showEmailTooltip()"
              target="email" triggers=""
              placement="right"
            >
              {{ $t('messages.MG002.message') }}
            </b-tooltip>
          </b-col>
          <div class="w-100"/>
          <b-col
            role="group"
            class="my-data__field-group"
            sm="9"
            md="7"
            lg="5"
            xl="4"
          >
            <label class="my-data__field-label" for="country-ddi">
              {{ $t('my-data.country-ddi') }}
            </label>
            <country-ddi-select
              id="country-ddi"
              v-model="userData.countryCode"
            />
          </b-col>
          <b-col
            role="group"
            class="my-data__field-group"
            sm="9"
            md="7"
            lg="5"
            xl="4"
          >
            <label class="my-data__field-label" for="phone">
              {{ $t('my-data.phone') }}
            </label>
            <phone-input
              id="phone"
              v-model="userData.phone"
              :state="$bsStateValidator($v.userData.phone.$error)"
              @input="$v.userData.phone.$touch()"
            />
            <b-tooltip
              :show="showPhoneTooltip()"
              target="phone" triggers=""
              placement="right"
            >
              {{ $t('messages.MA111.message') }}
            </b-tooltip>
          </b-col>
          <div class="w-100"/>
          <b-col class="form-section-title">
            {{ $t('my-data.access-data') }}
          </b-col>
          <div class="w-100"/>
          <b-col class="my-data__button-group">
            <b-button
              class="my-data__form-button"
              variant="primary"
              @click="changePassword()"
            >
              {{ $t('my-data.change-password') }}
            </b-button>
            <b-button
              class="my-data__form-button"
              variant="outline-primary"
              @click="changeSecretQuestion()"
            >
              {{ $t('my-data.new-secret-question') }}
            </b-button>
          </b-col>
          <div class="w-100"/>
          <b-col class="form-section-title">
            {{ $t('my-data.preferences') }}
          </b-col>
          <div class="w-100"/>
          <b-col>
            <b-form-checkbox v-model="userData.nightMode">
              {{ $t('my-data.activate-night-mode') }}
            </b-form-checkbox>
          </b-col>
          <div class="w-100"/>
          <div class="w-100"/>
          <b-col>
            <b-form-checkbox v-model="userData.feedbackButton">
              {{ $t('my-data.activate-feedback-button') }}
            </b-form-checkbox>
          </b-col>
          <div class="w-100"/>
          <b-col
            class="my-data__spaced-label"
          >
            <label>{{ $t('my-data.change-font-size') }}</label>
          </b-col>
          <div class="w-100"/>
          <b-col class="my-data__button-group">
            <b-button
              v-for="(name, index) in fontSizeNames"
              :key="index"
              class="my-data__form-button s-btn-no-custom"
              :class="manageFontSizesClasses[index]"
              :variant="getFontButtonClass(index)"
              @click="setFontSize(index)"
            >
              <small>
                <i
                  aria-hidden="true"
                  class="fas fa-font"
                />
                <strong>+</strong>
              </small>
              &nbsp;{{ $t('my-data.font-sizes.' + name) }}
            </b-button>
          </b-col>
        </b-row>
        <b-row class="my-data__footer-buttons-row">
          <b-col class="my-data__footer-buttons">
            <b-button
              class="my-data__form-button"
              variant="secondary"
              @click="cancel()"
            >
              {{ $t('general.cancel') }}
            </b-button>
            <b-button
              class="my-data__form-button"
              variant="primary"
              :disabled="!canSave"
              @click="save()"
            >
              {{ $t('general.save') }}
            </b-button>
          </b-col>
        </b-row>
      </b-container>
    </app-panel>
    <change-password-modal v-model="passwordModalShow"></change-password-modal>
    <change-secret-question-modal v-model="secretQuestionModalShow"></change-secret-question-modal>
  </app-content>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  required,
  minLength,
  maxLength,
  email,
} from 'vuelidate/lib/validators';
import GlobalUserService from '@/services/user';
import CountryDdiSelect from '@/domain/components/user/CountryDdiSelect';
import LanguageSelect from '@/domain/components/user/LanguageSelect';
import PhoneInput from '@/domain/components/user/PhoneInput';
import UserService from '@/domain/services/user';
import ChangePasswordModal from '@/domain/components/user/ChangePasswordModal';
import ChangeSecretQuestionModal from '@/domain/components/user/ChangeSecretQuestionModal';
import InfoBadge from '@/components/icon/InfoBadge';
import TimezoneSelect from '@/domain/components/user/TimezoneSelect';

export default {
  components: {
    PhoneInput,
    LanguageSelect,
    CountryDdiSelect,
    ChangePasswordModal,
    ChangeSecretQuestionModal,
    InfoBadge,
    TimezoneSelect,
  },
  data() {
    return {
      userData: {
        fontSize: null,
        language: null,
        countryCode: 'BR',
      },
      fontSizeNames: [
        'normal',
        'medium',
        'big',
      ],
      passwordModalShow: false,
      secretQuestionModalShow: false,
    };
  },
  validations: {
    userData: {
      name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(100),
      },
      surname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(200),
      },
      phone: {
        minLength: minLength(14),
      },
      email: {
        required,
        email,
      },
    },
  },
  computed: {
    ...mapGetters([
      'manageFontSizesClasses',
      'currentFontSize',
      'subMenu',
    ]),
    canSave() {
      const userDataValidation = this.$v.userData;
      return userDataValidation.name.required
        && userDataValidation.surname.required
        && userDataValidation.email.required;
    },
    fontSize() {
      return (this.userData && this.userData.fontSize) || this.currentFontSize;
    },
  },
  created() {
    UserService.getMyData().then((userData) => { this.userData = userData; });
  },
  methods: {
    getFontButtonClass(fontIndex) {
      return this.fontSize === this.manageFontSizesClasses[fontIndex]
        ? 'primary'
        : 'outline-primary';
    },
    setFontSize(fontIndex) {
      this.userData.fontSize = this.manageFontSizesClasses[fontIndex];
    },
    changePassword() {
      this.passwordModalShow = true;
    },
    changeSecretQuestion() {
      this.secretQuestionModalShow = true;
    },
    getInvalidFields() {
      const invalidFields = [];
      const validations = Object.entries(this.$v.userData);
      validations.forEach((validation) => {
        const value = validation[1] || {};
        if (value.$invalid) {
          const field = validation[0];
          invalidFields.push(this.$t(`my-data.${ field }`));
        }
      });
      return invalidFields;
    },
    save() {
      const invalidFields = this.getInvalidFields();
      if (invalidFields.length === 0) {
        this.putChanges().then(() => this.$router.push('/'));
        this.closeSubMenu();
        return;
      }
      this.$pushAlert({
        code: 'MA001',
        messageParams: {
          NomeDoCampo: invalidFields.join(', '),
        },
      });
    },
    cancel() {
      this.$confirmMessage({
        code: 'MC006',
      }).accepted(() => {
        this.$router.push('/');
        this.closeSubMenu();
      });
    },
    putChanges() {
      return UserService.saveMyData(this.userData).then(() => GlobalUserService.requestUserData());
    },
    showNameSurnameTooltip(validationModel) {
      return validationModel.$error && (
        !validationModel.required
        || !validationModel.minLength
      );
    },
    showEmailTooltip() {
      return this.$v.userData.email.$error
        && !this.$v.userData.email.email;
    },
    showPhoneTooltip() {
      return this.$v.userData.phone.$error
        && !this.$v.userData.phone.minLength;
    },
    closeSubMenu() {
      this.subMenu.items = [];
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.my-data
  &__field-tooltip
    ::v-deep .info-badge__icon
      color: var(--font-color-primary)
  &__spaced-label
    margin-top: 10px
  &__body
    padding-top: 10px
    padding-bottom: 10px
  &__fields
    > *
      margin-bottom: 5px
    > .form-section-title
      margin-top: 10px
  &__footer-buttons-row
    padding-top: 20px
  &__footer-buttons
    text-align: right
  +media-xs()
    &__button-group
      > *
        display: block
        &:not(:last-child)
          margin-bottom: 10px
  +media-min-sm()
    &__button-group
      > *:not(:last-child)
        margin-right: 10px

</style>
